// PaywallModal — Stripe checkout entry point + promo code support.
//
// Supports two promo code types:
//   • FREE (100% discount) — bypasses Stripe entirely, unlocks the user's
//     session via POST /api/promo/apply, then closes the modal.
//   • DISCOUNT_20 (20% off) — passed through to /payments/checkout, which
//     attaches a single-use Stripe Coupon to the checkout session.
import { useEffect, useState } from "react";
import { Lock, CreditCard, Check, Tag, Sparkles, RotateCcw } from "lucide-react";
import { createCheckout, getPricingTiers, validatePromo, applyPromo, restorePurchase } from "../lib/client";
import { trackEvent, rememberTier } from "../lib/analytics";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";

export default function PaywallModal({ open, onClose, usage, onUnlocked }) {
  const { t } = useI18n();
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState("small");
  const [loading, setLoading] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoStatus, setPromoStatus] = useState(null);
  const [validating, setValidating] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreEmail, setRestoreEmail] = useState("");
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (!open) return;
    // GA4: paywall opened — fire once per open.
    trackEvent("paywall_view", { remaining: usage?.remaining ?? null });
    getPricingTiers().then((data) => setTiers(data?.tiers || [])).catch(() => {});
  }, [open, usage?.remaining]);

  if (!open) return null;

  const currentPkg = tiers.find((t) => t.id === selectedTier) || null;

  async function handleCheckPromo() {
    const code = promoCode.trim().toUpperCase();
    if (!code) return;
    setValidating(true);
    setPromoStatus(null);
    try {
      const res = await validatePromo(code);
      setPromoStatus(res);
      if (!res.valid) {
        toast.error(t("promo_invalid"));
      } else if (res.type === "free") {
        trackEvent("promo_code_applied", { code, promo_type: "free" });
        toast.success(t("promo_free_detected"));
      } else if (res.type?.startsWith("discount") && res.percent_off > 0) {
        trackEvent("promo_code_applied", { code, promo_type: res.type, percent_off: res.percent_off });
        toast.success(`${t("promo_discount_detected")} (${res.percent_off}%)`);
      }
    } catch (e) {
      setPromoStatus({ valid: false, reason: "error" });
      toast.error(t("promo_invalid"));
    } finally {
      setValidating(false);
    }
  }

  async function handleRestore() {
    const em = restoreEmail.trim();
    if (!em) return;
    setRestoring(true);
    try {
      const data = await restorePurchase(em);
      if (data?.unlocked || data?.pro_plus_unlocked) {
        toast.success(t("restore_ok"));
        if (onUnlocked) onUnlocked();
        onClose();
      } else {
        toast.error(t("restore_none"));
      }
    } catch (e) {
      if (e?.response?.status === 404) toast.error(t("restore_none"));
      else if (e?.response?.status === 400) toast.error(t("restore_invalid"));
      else toast.error(t("restore_fail"));
    } finally {
      setRestoring(false);
    }
  }

  async function handlePay() {    setLoading(true);
    try {
      // If a FREE promo was validated, apply it and skip Stripe entirely.
      if (promoStatus?.valid && promoStatus.type === "free") {
        await applyPromo(promoCode.trim().toUpperCase());
        toast.success(t("promo_applied_unlocked"));
        if (onUnlocked) onUnlocked();
        onClose();
        return;
      }
      // Otherwise hit Stripe — pass discount code + selected tier.
      const code = promoStatus?.valid ? promoCode.trim().toUpperCase() : null;
      // GA4 begin_checkout — fired BEFORE the redirect so it lands
      // before the browser navigates away. Remember tier so
      // PaymentSuccess can fire `purchase` with the same context.
      if (currentPkg) {
        const finalAmount = isDiscountCode
          ? Number((currentPkg.amount * (100 - promoStatus.percent_off) / 100).toFixed(2))
          : currentPkg.amount;
        trackEvent("begin_checkout", {
          currency: "EUR",
          value: finalAmount,
          items: [{
            item_id: currentPkg.id,
            item_name: `Bingo ${currentPkg.id}`,
            price: finalAmount,
            quantity: 1,
          }],
          coupon: code || undefined,
        });
        rememberTier({ id: currentPkg.id, amount: finalAmount, currency: "EUR" });
      }
      const data = await createCheckout(code, selectedTier);
      if (data?.url) window.location.href = data.url;
      else { toast.error("Error"); setLoading(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.reason || "Error");
      setLoading(false);
    }
  }

  const price = currentPkg ? `€${currentPkg.amount.toFixed(2).replace(".", ",")}` : "...";
  const isFreeCode = promoStatus?.valid && promoStatus.type === "free";
  const isDiscountCode = promoStatus?.valid && promoStatus.type?.startsWith("discount") && promoStatus.percent_off > 0;
  const discountedPrice = currentPkg && isDiscountCode
    ? `€${(currentPkg.amount * (100 - promoStatus.percent_off) / 100).toFixed(2).replace(".", ",")}`
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-md" style={{ background: "rgba(17, 17, 17, 0.55)" }} data-testid="paywall-modal" role="dialog" aria-modal="true">
      <div className="nb-card-lg w-full max-w-lg rounded-md overflow-hidden bg-white">
        <div className="bg-[var(--brand-yellow)] px-6 py-4 flex items-center gap-3 border-b-2 border-black">
          <div className="w-10 h-10 nb-border rounded-md bg-white flex items-center justify-center"><Lock size={20} /></div>
          <div>
            <div className="text-xs font-bold uppercase tracking-widest">{t("pw_golden")}</div>
            <div className="font-display font-black text-2xl tracking-tight">{t("pw_unlock_all")}</div>
          </div>
        </div>
        <div className="p-6">
          {usage?.next_free_at ? (
            <div className="mb-4 nb-border rounded-md bg-[#FFF3D6] px-4 py-3 text-sm" data-testid="paywall-daily-limit">
              <b>{t("pw_daily_title")}</b><br />
              {t("pw_daily_body")} {usage?.seconds_until_reset ? `${t("free_next_in")} ${Math.ceil(usage.seconds_until_reset / 3600)}${t("hours_short")}.` : ""}
            </div>
          ) : (usage?.remaining > 0) ? (
            <p className="text-base text-neutral-700 mb-4" data-testid="paywall-intro-more">
              {t("pw_intro_more")}
            </p>
          ) : (
            <p className="text-base text-neutral-700 mb-4">
              {t("pw_intro")} <b>{usage?.free_limit || 20} {t("pw_intro_2")}</b> {t("pw_intro_3")}
            </p>
          )}

          {/* Tier picker — 3 clickable cards. Server is single source of
              truth for pricing (fetched from /api/pricing/tiers). */}
          <div className="grid gap-2 mb-5" data-testid="tier-picker">
            {tiers.map((tier) => {
              const isSelected = selectedTier === tier.id;
              const label = tier.max_cards
                ? `${t("tier_upto")} ${tier.max_cards} ${t("tier_cards")}`
                : t("tier_unlimited");
              const amount = `€${tier.amount.toFixed(2).replace(".", ",")}`;
              // Middle tier gets a "Popular" badge to nudge conversion
              const isPopular = tier.id === "medium";
              return (
                <button
                  key={tier.id}
                  type="button"
                  onClick={() => {
                    setSelectedTier(tier.id);
                    trackEvent("tier_selected", {
                      tier_id: tier.id,
                      value: tier.amount,
                      currency: "EUR",
                    });
                  }}
                  className={`relative nb-border rounded-md p-3 flex items-center gap-3 text-left transition-all ${
                    isSelected
                      ? "bg-[var(--brand-yellow)] shadow-[3px_3px_0_#000] -translate-x-[1px] -translate-y-[1px]"
                      : "bg-white hover:bg-neutral-50"
                  }`}
                  data-testid={`tier-option-${tier.id}`}
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 border-black flex-shrink-0 flex items-center justify-center ${
                      isSelected ? "bg-black" : "bg-white"
                    }`}
                  >
                    {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-black text-lg leading-tight">{label}</div>
                    <div className="text-[11px] text-neutral-600 font-semibold">{t("tier_per_session")}</div>
                  </div>
                  <div className="font-display font-black text-xl flex-shrink-0">{amount}</div>
                  {isPopular && (
                    <span className="absolute -top-2 right-3 text-[10px] font-black bg-[var(--brand-red)] text-white px-2 py-0.5 rounded-full uppercase tracking-widest border-2 border-black">
                      {t("tier_popular")}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <ul className="space-y-2 mb-5" data-testid="paywall-benefits">
            {/* Bullet 1 removed — "Unlimited cards" was misleading now that
                the small tier caps at 50. The selected tier card above
                already communicates the exact card allowance. */}
            {[t("pw_b2"), t("pw_b3"), t("pw_b4")].map((b) => (
              <li key={b} className="flex items-center gap-2 text-sm font-medium">
                <span className="w-5 h-5 nb-border rounded-full bg-[var(--brand-green)] text-white flex items-center justify-center"><Check size={14} strokeWidth={3} /></span>
                {b}
              </li>
            ))}
          </ul>

          {/* Promo code section */}
          <div className="mb-4 nb-border rounded-md p-3 bg-[var(--bg-highlight)]" data-testid="promo-section">
            {!promoOpen ? (
              <button
                type="button"
                onClick={() => setPromoOpen(true)}
                className="text-sm font-bold inline-flex items-center gap-1.5 underline underline-offset-2 hover:text-[var(--brand-red)]"
                data-testid="promo-open-toggle"
              >
                <Tag size={14} /> {t("promo_have_code")}
              </button>
            ) : (
              <div className="space-y-2">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-neutral-700">
                  {t("promo_code_label")}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoStatus(null); }}
                    placeholder="BINGO20-XXXXXX"
                    className="flex-1 nb-border rounded-md px-3 py-2 text-sm bg-white font-mono uppercase"
                    data-testid="promo-code-input"
                  />
                  <button
                    type="button"
                    onClick={handleCheckPromo}
                    disabled={validating || !promoCode.trim()}
                    className="nb-button bg-black text-white px-3 py-2 rounded-md text-sm font-bold disabled:opacity-60"
                    data-testid="promo-check-button"
                  >
                    {validating ? "..." : t("promo_check")}
                  </button>
                </div>
                {isFreeCode && (
                  <div className="text-xs font-bold text-[var(--brand-green)] flex items-center gap-1">
                    <Sparkles size={12} /> {t("promo_free_detected")}
                  </div>
                )}
                {isDiscountCode && (
                  <div className="text-xs font-bold text-[var(--brand-green)] flex items-center gap-1">
                    <Sparkles size={12} /> {t("promo_discount_detected")} ({promoStatus.percent_off}%)
                  </div>
                )}
                {promoStatus && !promoStatus.valid && (
                  <div className="text-xs font-bold text-[var(--brand-red)]">
                    ✗ {t("promo_invalid")}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Al betaald? Herstel je aankoop via het betaal-e-mailadres */}
          <div className="mb-4 nb-border rounded-md p-3 bg-white" data-testid="restore-section">
            {!restoreOpen ? (
              <button
                type="button"
                onClick={() => setRestoreOpen(true)}
                className="text-sm font-bold inline-flex items-center gap-1.5 underline underline-offset-2 hover:text-[var(--brand-red)]"
                data-testid="restore-open-toggle"
              >
                <RotateCcw size={14} /> {t("restore_cta")}
              </button>
            ) : (
              <div className="space-y-2">
                <label className="block text-[10px] font-bold uppercase tracking-widest text-neutral-700">
                  {t("restore_email_label")}
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={restoreEmail}
                    onChange={(e) => setRestoreEmail(e.target.value)}
                    placeholder={t("restore_email_ph")}
                    className="flex-1 nb-border rounded-md px-3 py-2 text-sm bg-white"
                    data-testid="restore-email-input"
                  />
                  <button
                    type="button"
                    onClick={handleRestore}
                    disabled={restoring || !restoreEmail.trim()}
                    className="nb-button bg-black text-white px-3 py-2 rounded-md text-sm font-bold disabled:opacity-60"
                    data-testid="restore-submit-button"
                  >
                    {restoring ? "..." : t("restore_btn")}
                  </button>
                </div>
                <p className="text-[11px] text-neutral-500">{t("restore_hint")}</p>
              </div>
            )}
          </div>

          <div className="flex items-baseline gap-2 mb-5" data-testid="paywall-price">
            {isFreeCode ? (
              <div className="font-display font-black text-3xl text-[var(--brand-green)]">€0,00</div>
            ) : isDiscountCode ? (
              <>
                <div className="font-display font-black text-3xl text-[var(--brand-green)]">{discountedPrice}</div>
                <div className="text-sm text-neutral-500 line-through font-bold">{price}</div>
              </>
            ) : (
              <div className="font-display font-black text-3xl">{t("tier_you_pay")} {price}</div>
            )}
            <div className="text-sm text-neutral-600 font-medium">{t("eenmalig")}</div>
          </div>
          <button onClick={handlePay} disabled={loading} className="nb-button bg-[var(--brand-red)] text-white w-full py-3 rounded-md flex items-center justify-center gap-2 text-base disabled:opacity-60" data-testid="paywall-pay-button">
            <CreditCard size={18} />
            {loading ? "..." : isFreeCode ? t("promo_unlock_free") : t("pw_pay")}
          </button>
          <button onClick={onClose} className="w-full mt-3 text-sm font-semibold text-neutral-600 underline underline-offset-4 hover:text-black" data-testid="paywall-close-button">
            {t("pw_later")}
          </button>
          <p className="text-[11px] text-neutral-500 mt-4 text-center">{t("pw_secure")}</p>
        </div>
      </div>
    </div>
  );
}
